import React, { useState, useEffect } from 'react';
import './Product.css';
import CTASection from '../components/cta/CTASection';

const Product: React.FC = () => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const fullText = 'The Complete Clinical Trial Infrastructure for LATAM';
  const typingSpeed = 80;

  useEffect(() => {
    if (currentIndex < fullText.length) {
      const timeoutId = setTimeout(() => {
        setDisplayedText(fullText.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, typingSpeed);

      return () => clearTimeout(timeoutId);
    }
  }, [currentIndex, fullText]);

  return (
    <div className="product-page">
      <section className="product-hero">
        <div className="hero-background">
          <div className="hero-background-image">
            <video autoPlay muted loop>
              <source src="/images/hero.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="hero-overlay"></div>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <h1>
              <span className="gradient-text typewriter-text">
                {displayedText}
                {currentIndex < fullText.length && <span className="typewriter-cursor">|</span>}
              </span>
            </h1>
            
            <p>
              From protocol upload to patient recruitment, regulatory compliance to data management. 
              One integrated platform that transforms how clinical trials operate across Latin America.
            </p>
            
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-value">95%</div>
                <div className="stat-label">Success Rate</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">2-4</div>
                <div className="stat-label">Weeks Setup</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">12</div>
                <div className="stat-label">LATAM Countries</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="platform-modules">
        <div className="container">
          <div className="section-header">
            <h2>
              <span className="gradient-text">Six Powerful Modules</span> Working as One
            </h2>
            <p>
              Each module is designed to solve specific clinical trial challenges, 
              but together they create an unstoppable force for your research success.
            </p>
          </div>
          
          <div className="modules-grid">
            <div className="module-card centralization">
              <div className="module-content">
                <h3>Site Centralization Hub</h3>
                <p>Unify all your research sites under one intelligent platform. Standardize processes, streamline communication, and coordinate trials across multiple locations with unprecedented efficiency.</p>
                <div className="module-benefit">
                  <span className="benefit-label">Network Effect:</span>
                  <span className="benefit-value">300% coordination improvement</span>
                </div>
              </div>
            </div>
            
            <div className="module-card matching">
              <div className="module-content">
                <h3>Intelligent Site & Patient Matching</h3>
                <p>Our advanced matching engine finds the perfect research sites and identifies ideal patient populations based on your specific protocol requirements and success patterns.</p>
                <div className="module-benefit">
                  <span className="benefit-label">Success Rate:</span>
                  <span className="benefit-value">95% enrollment success</span>
                </div>
              </div>
            </div>
            
            <div className="module-card compliance">
              <div className="module-content">
                <h3>Automatic Regulatory Compliance</h3>
                <p>Stay compliant across all 12 LATAM countries with real-time monitoring of COFEPRIS, ANVISA, INVIMA and other regulatory requirements. Never miss a deadline again.</p>
                <div className="module-benefit">
                  <span className="benefit-label">Coverage:</span>
                  <span className="benefit-value">100% regulatory compliance</span>
                </div>
              </div>
            </div>
            
            <div className="module-card payments">
              <div className="module-content">
                <h3>Automated Milestone Payments</h3>
                <p>Transparent, automated payments based on verified milestones. Build trust with sites and investigators through reliable, instant compensation when goals are achieved.</p>
                <div className="module-benefit">
                  <span className="benefit-label">Payment Speed:</span>
                  <span className="benefit-value">Instant milestone rewards</span>
                </div>
              </div>
            </div>
            
            <div className="module-card recruitment">
              <div className="module-content">
                <h3>Patient Recruitment & Protocol Follow-up</h3>
                <p>Intelligent recruitment agents work 24/7 to identify, screen, and engage potential patients, while automated systems ensure perfect protocol adherence throughout the entire trial lifecycle.</p>
                <div className="module-benefit">
                  <span className="benefit-label">Efficiency:</span>
                  <span className="benefit-value">85% conversion + 99% adherence</span>
                </div>
              </div>
            </div>
            
            <div className="module-card insights">
              <div className="module-content">
                <h3>Real-Time Strategic Insights</h3>
                <p>Live dashboards with predictive analytics help you make informed decisions before problems arise. Monitor recruitment health, compliance status, and financial performance in real-time.</p>
                <div className="module-benefit">
                  <span className="benefit-label">Visibility:</span>
                  <span className="benefit-value">100% real-time transparency</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="platform-summary">
            <div className="summary-content">
              <h3>The Result: Clinical Trials That Actually Work</h3>
              <p>
                When these six modules work together, magic happens. Your trials launch faster, 
                recruit better, stay compliant automatically, and deliver results that transform 
                healthcare across Latin America.
              </p>
              <div className="summary-metrics">
                <div className="metric">
                  <span className="metric-value">300%</span>
                  <span className="metric-label">Faster Launch</span>
                </div>
                <div className="metric">
                  <span className="metric-value">95%</span>
                  <span className="metric-label">Success Rate</span>
                </div>
                <div className="metric">
                  <span className="metric-value">Zero</span>
                  <span className="metric-label">Compliance Issues</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Product; 